import { useState } from "react";
import { Service } from "@/types/dashboard";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { supabase } from "@/integrations/supabase/client";
import { updateServiceVisibility } from "@/utils/serviceUtils";
import { useNavigate } from "react-router-dom";

export const useServiceActions = (
  setLocalServices: React.Dispatch<React.SetStateAction<Service[]>>,
  onDeleteService: (serviceId: string) => Promise<boolean>,
  refreshServices: () => Promise<void>
) => {
  const [isDeleting, setIsDeleting] = useState<{[key: string]: boolean}>({});
  const [previewService, setPreviewService] = useState<Service | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [serviceFormOpen, setServiceFormOpen] = useState(false);
  const [isLoadingService, setIsLoadingService] = useState(false);
  const { toast } = useToast();
  const { trackAction } = useAnalytics({ component: "Services" });
  const navigate = useNavigate();

  const handleToggleVisibility = async (service: Service) => {
    try {
      const newVisibilityStatus = !service.is_active;
      
      const success = await updateServiceVisibility(service.id, newVisibilityStatus);
      
      if (success) {
        // Update local state first
        setLocalServices(prevServices => 
          prevServices.map(s => 
            s.id === service.id ? { ...s, is_active: newVisibilityStatus } : s
          )
        );
        
        // Then refresh services to ensure consistency
        await refreshServices();
        
        toast({
          title: "Success",
          description: `Service ${newVisibilityStatus ? "visible" : "hidden"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Error toggling service visibility:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update service visibility. Please try again.",
      });
    }
  };

  const fetchServiceDetails = async (serviceId: string): Promise<Service | null> => {
    try {
      setIsLoadingService(true);
      console.log("Fetching service details for edit, ID:", serviceId);
      
      const { data, error } = await supabase
        .from('services')
        .select(`
          *,
          service_categories (
            id,
            name
          )
        `)
        .eq('id', serviceId)
        .single();
      
      if (error) {
        console.error("Error fetching service for edit:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load service details. Please try again.",
        });
        return null;
      }
      
      console.log("Successfully fetched service for edit:", data);
      return data as Service;
    } catch (error) {
      console.error("Exception in fetchServiceDetails:", error);
      return null;
    } finally {
      setIsLoadingService(false);
    }
  };

  const handleEditService = async (service: Service) => {
    trackAction('edit_service', { serviceId: service.id });
    
    // Set loading state immediately
    setIsLoadingService(true);
    
    try {
      // Fetch the service details before showing the form
      const freshServiceData = await fetchServiceDetails(service.id);
      
      if (freshServiceData) {
        console.log("Setting selected service with fresh data:", freshServiceData);
        setSelectedService(freshServiceData);
        // Only open the form dialog after successfully fetching the data
        setServiceFormOpen(true);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Unable to load service details. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error in handleEditService:", error);
      toast({
        variant: "destructive",
        title: "Error", 
        description: "Failed to load service details. Please try again."
      });
    } finally {
      setIsLoadingService(false);
    }
  };

  const handleAddNewService = () => {
    setSelectedService(null);
    setServiceFormOpen(true);
  };

  const handleDeleteService = async (serviceId: string) => {
    setIsDeleting(prev => ({ ...prev, [serviceId]: true }));
    try {
      const success = await onDeleteService(serviceId);
      setIsDeleting(prev => ({ ...prev, [serviceId]: false }));
      if (success) {
        // Update local state first for immediate UI feedback
        setLocalServices(prevServices => prevServices.filter(service => service.id !== serviceId));
        
        // Then refresh services to ensure we have the latest data
        await refreshServices();
      }
      return success;
    } catch (error) {
      setIsDeleting(prev => ({ ...prev, [serviceId]: false }));
      return false;
    }
  };

  const handleServiceFormSuccess = async () => {
    console.log("Service form success, refreshing services");
    
    try {
      await refreshServices();
      console.log("Services refreshed successfully");
      
      // After successful refresh, add a refresh parameter to the URL
      // Using replace: true to avoid navigation history issues
      navigate('/dashboard/services?refresh=true', { replace: true });
    } catch (error) {
      console.error("Error during service form success handling:", error);
    }
  };

  return {
    isDeleting,
    previewService,
    setPreviewService,
    selectedService,
    serviceFormOpen,
    setServiceFormOpen,
    isLoadingService,
    handleToggleVisibility,
    handleEditService,
    handleAddNewService,
    handleDeleteService,
    handleServiceFormSuccess
  };
};
